import { getSystemFlags } from 'core/core.selectors';
import type { Feature, GeoJsonProperties, Point } from 'geojson';
import type { MapLayerMouseEvent } from 'mapbox-gl';
import mapboxgl from 'mapbox-gl';
import FieldDetailsCard from '@features/regional-overview/components/field-details-card/field-details-card.component';
import { FieldDetailsContent } from '@features/regional-overview/components/field-details-card/field-details-content.component';
import { FieldDetailsPopupStyles } from '@features/regional-overview/components/field-details-card/field-details-card.styles';
import { useRegionalOverviewFieldNavigate } from '@features/regional-overview/utils';
import { type RegionalOverviewV2Properties, type RegionalOverviewV2Values } from 'querys/regional-overview/regional-overview.query.models';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAbioticStressVisibility } from '../regional-risk-overview/use-abiotic-stress-visibility.hook';
import { createHoverHandlers, setFeatureHoverState } from '../utils/hover.utils';
import { calculatePopupAnchor, createCropFromValues, createFieldInfoItems, parseFeatureValues } from '../utils/popup.utils';
import { FIELD_LAYER, OVERVIEW_POINTS_LAYER } from './use-render-map-disease-risk-field';

interface PopupState {
  properties: RegionalOverviewV2Properties;
  values: RegionalOverviewV2Values;
  lngLat: mapboxgl.LngLat;
  clickCoordinates: { vertical: number; horizontal: number };
}

const useMapCursor = (map: mapboxgl.Map | undefined, flags: ReturnType<typeof getSystemFlags>) => {
  const hoveredFeatureIdRef = useRef<string | number | null>(null);

  useEffect(() => {
    if (!map || !flags?.P40_54240_enable_overview_points_popup) return () => undefined;

    const featureHandlers = createHoverHandlers(map, hoveredFeatureIdRef, setFeatureHoverState);

    map.on('mousemove', [OVERVIEW_POINTS_LAYER, FIELD_LAYER], featureHandlers.onMouseMove);
    map.on('mouseleave', [OVERVIEW_POINTS_LAYER, FIELD_LAYER], featureHandlers.onMouseLeave);

    return () => {
      map.off('mousemove', [OVERVIEW_POINTS_LAYER, FIELD_LAYER], featureHandlers.onMouseMove);
      map.off('mouseleave', [OVERVIEW_POINTS_LAYER, FIELD_LAYER], featureHandlers.onMouseLeave);

      featureHandlers.onMouseLeave();
    };
  }, [map, flags]);
};

export interface FieldPopupData {
  properties: RegionalOverviewV2Properties;
  coordinates: [number, number];
}

interface UseOverviewPointsPopupReturn {
  portalContent: ReactNode;
  openFieldPopup: (data: FieldPopupData) => void;
  isOpen: boolean;
  content: ReactNode;
  onClose: () => void;
}

interface UseOverviewPointsPopupProps<T extends GeoJsonProperties> {
  map?: mapboxgl.Map;
  mapPointFeatures?: Feature<Point, T>[];
  isMobile?: boolean;
}

export const useOverviewPointsPopup = <T extends GeoJsonProperties>({
  map,
  mapPointFeatures,
  isMobile = false
}: UseOverviewPointsPopupProps<T>): UseOverviewPointsPopupReturn => {
  const [t] = useTranslation();
  const flags = useSelector(getSystemFlags);
  const handleAccessDetails = useRegionalOverviewFieldNavigate();
  const { showAbioticStress } = useAbioticStressVisibility();
  const [popupState, setPopupState] = useState<PopupState | null>(null);
  const popupContainerRef = useRef<HTMLDivElement | null>(null);
  const mapPopupRef = useRef<mapboxgl.Popup | null>(null);

  useEffect(() => {
    popupContainerRef.current = document.createElement('div');

    return () => {
      popupContainerRef.current = null;
    };
  }, []);

  useMapCursor(map, flags);

  const handleClose = useCallback(() => {
    setPopupState(null);
    mapPopupRef.current?.remove();
    mapPopupRef.current = null;
  }, []);

  const openFieldPopup = useCallback(
    (data: FieldPopupData) => {
      if (!map || !popupContainerRef.current) return;

      const parsedProperties = parseFeatureValues(data.properties);
      if (!parsedProperties?.values) return;

      const container = map.getContainer();
      const centerX = container.clientWidth / 2;
      const centerY = container.clientHeight / 2;

      setPopupState({
        properties: parsedProperties,
        values: parsedProperties.values,
        lngLat: new mapboxgl.LngLat(data.coordinates[0], data.coordinates[1]),
        clickCoordinates: { vertical: centerY, horizontal: centerX }
      });
    },
    [map]
  );

  const handleClick = useCallback(
    (e: MapLayerMouseEvent) => {
      if (!map || !popupContainerRef.current || !e.lngLat || !e.features?.[0]?.properties) return;

      const feature = e.features[0];
      const properties = feature.properties as RegionalOverviewV2Properties;
      const parsedProperties = parseFeatureValues(properties);

      if (!parsedProperties?.values) return;

      setPopupState({
        properties: parsedProperties,
        values: parsedProperties.values,
        lngLat: e.lngLat,
        clickCoordinates: { vertical: e.point.y, horizontal: e.point.x }
      });
    },
    [map]
  );

  useEffect(() => {
    handleClose();
  }, [mapPointFeatures, handleClose]);

  useEffect(() => {
    if (!map || !popupContainerRef.current || !popupState || isMobile) return;

    mapPopupRef.current?.remove();

    const container = map.getContainer();
    const { anchor, offset } = calculatePopupAnchor({
      clickCoordinates: popupState.clickCoordinates,
      containerSize: { width: container.clientWidth, height: container.clientHeight }
    });

    mapPopupRef.current = new mapboxgl.Popup({
      closeButton: false,
      anchor,
      offset,
      className: 'field-details-popup'
    });

    mapPopupRef.current.setLngLat(popupState.lngLat).setDOMContent(popupContainerRef.current).addTo(map);
  }, [map, popupState, isMobile]);

  useEffect(() => {
    if (!map || !flags?.P40_54240_enable_overview_points_popup) return () => undefined;

    map.on('click', [OVERVIEW_POINTS_LAYER, FIELD_LAYER], handleClick);

    return () => {
      map.off('click', [OVERVIEW_POINTS_LAYER, FIELD_LAYER], handleClick);
    };
  }, [map, handleClick, flags]);

  const fieldDetailsProps = useMemo(() => {
    if (!popupState) return null;

    const { properties, values } = popupState;
    const crop = createCropFromValues(values);
    const fieldInfoItems = createFieldInfoItems(values, t);
    const stressRiskLevel = values.risks.stress_risk_level?.level;

    const onAccessDetails = () => {
      handleAccessDetails(values.org_id, values.property_id, properties.id, !!values.error);
    };

    return {
      farmName: values.property_name,
      fieldName: properties.name,
      area: values.field_area,
      riskLevel: values.risks.risk,
      crop,
      fieldInfoItems,
      diseases: values.risks.diseases,
      stressRiskLevel,
      showAbioticStress,
      onClose: handleClose,
      onAccessDetails,
      error: values.error
    };
  }, [popupState, t, handleClose, handleAccessDetails, showAbioticStress]);

  const portalContent = useMemo(() => {
    if (isMobile || !fieldDetailsProps || !popupContainerRef.current) return null;

    return createPortal(
      <>
        <FieldDetailsPopupStyles />
        <FieldDetailsCard {...fieldDetailsProps} />
      </>,
      popupContainerRef.current
    );
  }, [isMobile, fieldDetailsProps]);

  const content = useMemo(() => {
    if (!isMobile || !fieldDetailsProps) return null;

    return <FieldDetailsContent {...fieldDetailsProps} inSheet />;
  }, [isMobile, fieldDetailsProps]);

  return { portalContent, openFieldPopup, isOpen: !!popupState, content, onClose: handleClose };
};
